/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef } from 'react';

import { css, jsx, keyframes } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token, useThemeObserver } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive, type TooltipPrimitiveProps } from '@atlaskit/tooltip';

import { LabsIcon } from './assets/labs-icon';

const i18n = defineMessages({
	tooltip: {
		id: 'ai-common.ai-labs-lozenge.tooltip',
		defaultMessage: 'Beta: This feature is still in development.',
		description:
			'Tooltip message when the user hovers over the AI Labs lozenge letting them know that the features are experimental',
	},
});

const lozengeStyles = cssMap({
	lozenge: {
		paddingInline: token('space.050'),
		borderRadius: token('radius.small'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	tooltip: {
		// These are all default Tooltip styles except for maxWidth (originally 240px)
		boxSizing: 'border-box',
		maxWidth: '170px',
		backgroundColor: token('color.background.neutral.bold'),
		borderRadius: token('radius.small'),
		color: token('color.text.inverse'),
		font: token('font.body.small'),
		insetBlockStart: token('space.0'),
		insetInlineStart: token('space.0'),
		overflowWrap: 'break-word',
		paddingBlockEnd: token('space.025'),
		paddingBlockStart: token('space.025'),
		paddingInlineEnd: token('space.075'),
		paddingInlineStart: token('space.075'),
		wordWrap: 'break-word',
	},
});

const gradientWaveKeyframes = keyframes({
	'0%': {
		backgroundPosition: '0% 50%',
		backgroundSize: '200% 100%',
	},
	'25%': {
		backgroundPosition: '50% 30%',
		backgroundSize: '250% 100%',
	},
	'50%': {
		backgroundPosition: '100% 50%',
		backgroundSize: '200% 100%',
	},
	'75%': {
		backgroundPosition: '50% 70%',
		backgroundSize: '250% 100%',
	},
	'100%': {
		backgroundPosition: '0% 50%',
		backgroundSize: '200% 100%',
	},
});

const sampResetStyles = css({
	font: 'inherit',
});

const lozengeAnimatedBorder = css({
	position: 'relative',
	backgroundColor: token('color.background.neutral.subtle'),
	borderRadius: token('radius.small'),
	paddingInline: token('space.050'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 0, // ensures stacking context for pseudo-element

	// Create animated gradient border with pseudo-element
	'&::before': {
		content: '""',
		position: 'absolute',
		inset: 0,
		borderRadius: token('radius.small'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '1px',
		background: `linear-gradient(270deg, ${token('color.background.accent.yellow.subtlest.pressed')} 0%, ${token('color.background.accent.magenta.subtlest.pressed')} 100%)`,
		backgroundSize: '200% 100%',
		animation: `${gradientWaveKeyframes} 4s ease-in-out infinite`,
		WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
		WebkitMaskComposite: 'xor',
		maskComposite: 'exclude',
		pointerEvents: 'none',
		zIndex: -1,
	},
});

// Using a custom tooltip is a workaround to avoid odd spacing due to default styles,
// primarily for the English locale
const CustomTooltip = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function CustomTooltip(
	{ children, ...props },
	ref,
) {
	return (
		// CSS prop usage is in accordance with official guidance for customizing tooltips
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
		<TooltipPrimitive {...props} ref={ref} css={lozengeStyles.tooltip}>
			{children}
		</TooltipPrimitive>
	);
}) as React.ForwardRefExoticComponent<TooltipPrimitiveProps>;

export const AILabsLozenge = (): JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	// We don't want to i18n the term 'AI Labs' because it is the branding name
	// and should be the same across all languages
	return (
		<Tooltip component={CustomTooltip} content={formatMessage(i18n.tooltip)}>
			<div css={[lozengeStyles.lozenge, lozengeAnimatedBorder]} data-testid="ai-labs-lozenge">
				<Inline grow="hug" space="space.025" alignBlock="center">
					<LabsIcon darkMode={colorMode === 'dark'} />
					<div>
						<Text size="small" weight="semibold" color="color.text">
							<samp css={sampResetStyles}>LABS</samp>
						</Text>
					</div>
				</Inline>
			</div>
		</Tooltip>
	);
};
